import React from "react";
import {
  UseFormRegister,
  Control,
  FieldErrors,
  UseFieldArrayReturn,
} from "react-hook-form";
import {
  AlertCircle,
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
} from "lucide-react";
import ProjectFormSection from "./ProjectFormSection";

interface ProjectFormResponsibilitiesProps {
  register: UseFormRegister<any>;
  control: Control<any>;
  errors: FieldErrors<any>;
  fieldArray: UseFieldArrayReturn<any, "responsibilities", "id">;
  isActive: boolean;
  onToggle: () => void;
}

const ProjectFormResponsibilities: React.FC<
  ProjectFormResponsibilitiesProps
> = ({ register, errors, fieldArray, isActive, onToggle }) => {
  const { fields, append, remove } = fieldArray;

  return (
    <ProjectFormSection
      id="responsibilities"
      title="Responsibilities"
      description="Define what contributors will do"
      icon={Sparkles}
      iconGradient="from-amber-500 to-orange-500"
      isActive={isActive}
      onToggle={onToggle}
      badge={`${fields.length} item${fields.length !== 1 ? "s" : ""}`}
    >
      {(errors.responsibilities as any)?.root && (
        <div className="p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl flex items-center gap-2 text-red-600 text-xs sm:text-sm">
          <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          {(errors.responsibilities as any).root.message}
        </div>
      )}

      {fields.map((field, index) => (
        <div
          key={field.id}
          className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl group"
        >
          <div className="flex items-center gap-1.5 sm:gap-2 mt-2">
            <GripVertical className="w-4 h-4 text-gray-400 cursor-grab hidden sm:block" />
            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#8B52FF]/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] sm:text-xs font-medium text-[#8B52FF]">
                {index + 1}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <textarea
              {...register(`responsibilities.${index}.description`)}
              rows={2}
              placeholder="Describe the responsibility..."
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border-2 transition-all focus:outline-none resize-none ${
                (errors.responsibilities as any)?.[index]?.description
                  ? "border-red-300 focus:border-red-500 bg-red-50"
                  : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 bg-white"
              }`}
            />
            {(errors.responsibilities as any)?.[index]?.description && (
              <p className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="line-clamp-2">
                  {
                    (errors.responsibilities as any)[index]?.description
                      ?.message
                  }
                </span>
              </p>
            )}
          </div>
          {fields.length > 1 && (
            <button
              type="button"
              onClick={() => remove(index)}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors sm:opacity-0 sm:group-hover:opacity-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          append({
            order: fields.length + 1,
            description: "",
          })
        }
        className="w-full p-2.5 sm:p-3 border-2 border-dashed border-gray-300 rounded-lg sm:rounded-xl text-gray-500 hover:border-[#8B52FF] hover:text-[#8B52FF] transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
      >
        <Plus className="w-4 h-4" />
        Add Responsibility
      </button>
    </ProjectFormSection>
  );
};

export default ProjectFormResponsibilities;
